/**
 * ForgeUI — Shared Design System Components
 *
 * The Fuel tab is the visual benchmark for the entire Forge // Telemetry app.
 * This file extracts its five core design patterns into reusable components so
 * every tab can adopt the same visual language consistently.
 *
 * Usage:
 *   import { ForgeListRow, ForgePill, ForgeSectionHeader, ForgeFilterPillRow, ForgeMetricDisplay } from '@/components/ui/ForgeUI';
 */

import React from 'react';

// ─── Color System ─────────────────────────────────────────────────────────────

/**
 * Forge color tokens. Each key maps to a hex value and a set of Tailwind-safe
 * class strings for text, border, background, and progress bar usage.
 */
export type ForgeColor =
  | 'legs'
  | 'chest'
  | 'pull'
  | 'push'
  | 'arms-shoulders'
  | 'core'
  | 'rest'
  | 'brand'
  | 'orange'
  | 'blue'
  | 'yellow'
  | 'red'
  | 'green'
  | 'teal'
  | 'purple'
  | 'grey';

interface ColorTokens {
  hex: string;
  text: string;       // e.g. "text-orange-400"
  border: string;     // e.g. "border-orange-400/70"
  bg: string;         // e.g. "bg-orange-400/10"
  bar: string;        // e.g. "bg-orange-400" — for progress bars
  cssVar: string;     // raw CSS color for inline styles
}

const COLOR_MAP: Record<ForgeColor, ColorTokens> = {
  legs:            { hex: '#F97316', text: 'text-orange-400',  border: 'border-orange-400/70',  bg: 'bg-orange-400/10',  bar: 'bg-orange-400',  cssVar: 'rgb(249 115 22)' },
  chest:           { hex: '#F43F5E', text: 'text-rose-400',    border: 'border-rose-400/70',    bg: 'bg-rose-400/10',    bar: 'bg-rose-400',    cssVar: 'rgb(244 63 94)' },
  pull:            { hex: '#3B82F6', text: 'text-blue-400',    border: 'border-blue-400/70',    bg: 'bg-blue-400/10',    bar: 'bg-blue-400',    cssVar: 'rgb(59 130 246)' },
  push:            { hex: '#22C55E', text: 'text-green-400',   border: 'border-green-400/70',   bg: 'bg-green-400/10',   bar: 'bg-green-400',   cssVar: 'rgb(34 197 94)' },
  'arms-shoulders':{ hex: '#14B8A6', text: 'text-teal-400',    border: 'border-teal-400/70',    bg: 'bg-teal-400/10',    bar: 'bg-teal-400',    cssVar: 'rgb(20 184 166)' },
  core:            { hex: '#A855F7', text: 'text-purple-400',  border: 'border-purple-400/70',  bg: 'bg-purple-400/10',  bar: 'bg-purple-400',  cssVar: 'rgb(168 85 247)' },
  rest:            { hex: '#6B7280', text: 'text-[#6B7280]',   border: 'border-[#9CA3AF]',   bg: 'bg-[#9CA3AF]/10',   bar: 'bg-[#9CA3AF]',   cssVar: 'rgb(107 114 128)' },
  brand:           { hex: '#1B4332', text: 'text-emerald-900', border: 'border-emerald-900/70', bg: 'bg-emerald-900/10', bar: 'bg-emerald-900', cssVar: 'rgb(27 67 50)' },
  // Aliases for direct color names used in NutritionTracker
  orange:          { hex: '#F97316', text: 'text-orange-400',  border: 'border-orange-400/70',  bg: 'bg-orange-400/10',  bar: 'bg-orange-400',  cssVar: 'rgb(249 115 22)' },
  blue:            { hex: '#3B82F6', text: 'text-blue-400',    border: 'border-blue-400/70',    bg: 'bg-blue-400/10',    bar: 'bg-blue-400',    cssVar: 'rgb(59 130 246)' },
  yellow:          { hex: '#EAB308', text: 'text-yellow-400',  border: 'border-yellow-400/70',  bg: 'bg-yellow-400/10',  bar: 'bg-yellow-400',  cssVar: 'rgb(234 179 8)' },
  red:             { hex: '#EF4444', text: 'text-red-400',     border: 'border-red-400/70',     bg: 'bg-red-400/10',     bar: 'bg-red-400',     cssVar: 'rgb(239 68 68)' },
  green:           { hex: '#22C55E', text: 'text-green-400',   border: 'border-green-400/70',   bg: 'bg-green-400/10',   bar: 'bg-green-400',   cssVar: 'rgb(34 197 94)' },
  teal:            { hex: '#14B8A6', text: 'text-teal-400',    border: 'border-teal-400/70',    bg: 'bg-teal-400/10',    bar: 'bg-teal-400',    cssVar: 'rgb(20 184 166)' },
  purple:          { hex: '#A855F7', text: 'text-purple-400',  border: 'border-purple-400/70',  bg: 'bg-purple-400/10',  bar: 'bg-purple-400',  cssVar: 'rgb(168 85 247)' },
  grey:            { hex: '#6B7280', text: 'text-[#6B7280]',   border: 'border-[#9CA3AF]',   bg: 'bg-[#9CA3AF]/10',   bar: 'bg-[#9CA3AF]',   cssVar: 'rgb(107 114 128)' },
};

/** Resolve a ForgeColor key to its tokens, with a safe fallback. */
export function forgeColor(color: ForgeColor): ColorTokens {
  return COLOR_MAP[color] ?? COLOR_MAP['grey'];
}

// ─── 1. ForgeListRow ──────────────────────────────────────────────────────────

/**
 * Clean list row with a 3px left accent border in a category color.
 * Matches the Fuel tab food entry rows exactly.
 *
 * Props:
 *   color       — ForgeColor key for the left accent border
 *   title       — Bold primary label (left side, top)
 *   subtitle    — Smaller secondary info (left side, bottom)
 *   action      — Optional right-side slot (e.g. X button, gear icon)
 *   onClick     — Optional click handler for the entire row
 *   className   — Additional classes
 */
export interface ForgeListRowProps {
  color?: ForgeColor;
  accentCss?: string;         // raw CSS color override (e.g. from getSplitAccentColor)
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode; // extra content below subtitle
}

export function ForgeListRow({
  color,
  accentCss,
  title,
  subtitle,
  action,
  onClick,
  className = '',
  children,
}: ForgeListRowProps) {
  const accent = accentCss ?? (color ? forgeColor(color).cssVar : 'rgb(107 114 128 / 0.5)');
  return (
    <div
      className={`group flex items-center gap-2 bg-card rounded-sm transition-colors ${onClick ? 'cursor-pointer hover:bg-secondary/10' : ''} ${className}`}
      style={{
        borderLeft: `3px solid ${accent}`,
        borderTop: '1px solid hsl(var(--border) / 0.25)',
        borderRight: '1px solid hsl(var(--border) / 0.25)',
        borderBottom: '1px solid hsl(var(--border) / 0.25)',
        paddingLeft: '10px',
        paddingRight: '8px',
        paddingTop: '9px',
        paddingBottom: '9px',
      }}
      onClick={onClick}
    >
      {/* Left content */}
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="font-bold text-sm text-foreground leading-none truncate">{title}</div>
        {subtitle && (
          <div className="text-[10px] font-mono text-muted-foreground leading-snug">{subtitle}</div>
        )}
        {children}
      </div>
      {/* Right action slot */}
      {action && (
        <div className="shrink-0 flex items-center" onClick={(e) => e.stopPropagation()}>
          {action}
        </div>
      )}
    </div>
  );
}

// ─── 2. ForgePill ─────────────────────────────────────────────────────────────

/**
 * Outlined pill badge — transparent background, colored border, colored text.
 * Fully rounded (border-radius: 9999px). Compact padding.
 * Matches the muscle group labels in SplitManager and the category pills in Fuel.
 *
 * Props:
 *   color   — ForgeColor key
 *   label   — Text to display
 *   solid   — When true, renders as solid fill (active state in filter rows)
 *   size    — 'sm' (default) | 'xs'
 */
export interface ForgePillProps {
  color?: ForgeColor;
  label: string;
  solid?: boolean;
  size?: 'xs' | 'sm';
  className?: string;
  onClick?: () => void;
}

export function ForgePill({ color = 'grey', label, solid = false, size = 'sm', className = '', onClick }: ForgePillProps) {
  const tokens = forgeColor(color);
  const sizeClasses = size === 'xs'
    ? 'px-1.5 py-0.5 text-[8px]'
    : 'px-2 py-0.5 text-[9px]';

  if (solid) {
    return (
      <span
        className={`inline-flex items-center rounded-full font-mono font-semibold uppercase tracking-wider ${sizeClasses} ${tokens.bar} text-white ${onClick ? 'cursor-pointer' : ''} ${className}`}
        style={{ lineHeight: 1.4 }}
        onClick={onClick}
      >
        {label}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center rounded-full border font-mono font-semibold uppercase tracking-wider bg-transparent ${sizeClasses} ${tokens.border} ${tokens.text} ${onClick ? 'cursor-pointer' : ''} ${className}`}
      style={{ lineHeight: 1.4 }}
      onClick={onClick}
    >
      {label}
    </span>
  );
}

// ─── 3. ForgeSectionHeader ────────────────────────────────────────────────────

/**
 * Uppercase, small, letter-spaced section label.
 * Matches "DAILY TOTALS · 5 ENTRIES" in the Fuel tab.
 *
 * Props:
 *   label     — Main label text (auto-uppercased)
 *   count     — Optional count appended as "· N items"
 *   action    — Optional right-side slot (e.g. gear button)
 *   className — Additional classes
 */
export interface ForgeSectionHeaderProps {
  label: string;
  count?: number;
  countLabel?: string;   // e.g. "entry" / "entries"
  noPlural?: boolean;    // When true, never append 's' to countLabel (e.g. "LOADED")
  action?: React.ReactNode;
  className?: string;
}

export function ForgeSectionHeader({ label, count, countLabel = 'item', noPlural = false, action, className = '' }: ForgeSectionHeaderProps) {
  const countStr = count !== undefined
    ? ` · ${count} ${noPlural ? countLabel : (count === 1 ? countLabel : countLabel + 's')}`
    : '';
  return (
    <div className={`flex items-center justify-between ${className}`}>
      <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">
        {label.toUpperCase()}{countStr}
      </span>
      {action && <div>{action}</div>}
    </div>
  );
}

// ─── 4. ForgeFilterPillRow ────────────────────────────────────────────────────

/**
 * Horizontal scrollable row of filter pills.
 * Active pill = solid dark primary fill + white text.
 * Inactive = outlined, grey border, grey text.
 *
 * Matches the category filter pills in the Fuel tab's Food Library modal.
 *
 * Props:
 *   options   — Array of { key, label } filter options
 *   value     — Currently active key
 *   onChange  — Called with the new key when a pill is clicked
 *   className — Additional classes
 */
export interface ForgeFilterOption {
  key: string;
  label: string;
}

export interface ForgeFilterPillRowProps {
  options: ForgeFilterOption[];
  value: string;
  onChange: (key: string) => void;
  className?: string;
}

export function ForgeFilterPillRow({ options, value, onChange, className = '' }: ForgeFilterPillRowProps) {
  return (
    <div className={`flex gap-1.5 overflow-x-auto pb-1 scrollbar-none shrink-0 ${className}`}>
      {options.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          className={`shrink-0 px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider border transition-colors ${
            value === opt.key
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-secondary/30 text-muted-foreground border-border/40 hover:border-primary/50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── 5. ForgeMetricDisplay ────────────────────────────────────────────────────

/**
 * Large bold number in a brand color, smaller unit label beside it,
 * optional progress bar below in matching color.
 *
 * Matches the calorie hero number in the Fuel tab's Daily Totals panel.
 *
 * Props:
 *   value       — Current numeric value (displayed as Math.round)
 *   goal        — Optional goal value (shows "/ goal unit" and progress bar)
 *   unit        — Unit label (e.g. "KCAL", "g")
 *   color       — ForgeColor key for text and progress bar
 *   size        — 'lg' (default, 3xl) | 'md' (xl) | 'sm' (base)
 *   showBar     — Show progress bar (default true when goal is set)
 *   className   — Additional classes
 */
export interface ForgeMetricDisplayProps {
  value: number;
  goal?: number;
  unit?: string;
  color?: ForgeColor;
  size?: 'sm' | 'md' | 'lg';
  showBar?: boolean;
  className?: string;
}

export function ForgeMetricDisplay({
  value,
  goal,
  unit = '',
  color = 'orange',
  size = 'lg',
  showBar = true,
  className = '',
}: ForgeMetricDisplayProps) {
  const tokens = forgeColor(color);
  const pct = goal && goal > 0 ? Math.min(100, Math.round((value / goal) * 100)) : 0;
  const overGoal = goal ? value > goal : false;

  const sizeClass = size === 'lg' ? 'text-3xl' : size === 'md' ? 'text-xl' : 'text-base';

  return (
    <div className={`text-center ${className}`}>
      <div>
        <span className={`font-mono font-black ${sizeClass} ${tokens.text}`}>
          {Math.round(value)}
        </span>
        {(goal || unit) && (
          <span className="font-mono text-sm text-muted-foreground ml-1">
            {goal ? `/ ${goal}` : ''} {unit}
          </span>
        )}
      </div>
      {showBar && goal && (
        <div className="w-full bg-secondary/40 rounded-full h-1 mt-1.5">
          <div
            className={`h-1 rounded-full transition-all ${overGoal ? tokens.bar : pct >= 70 ? 'bg-green-500' : 'bg-zinc-500'}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}
